import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";
import { GET_DOCUMENT, GET_ID_DOCUMENT, SUBMIT_DOCUMENT, SUMMARY_DOCUMENT, UPLOAD_DOCUMENT } from "src/config/apiConfig";

// Upload document
export const uploadDocumentThunk = createAsyncThunk(
    'document/uploadDocumentThunk',
    async (file: File, thunkAPI) => {
        try {
            const accessToken = localStorage.getItem('userToken');
            const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(UPLOAD_DOCUMENT, formData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                },
            });
            if (response.status === 200) {
                toast.success(response.data.message);
                return { data: response.data.data };
            } else {
                toast.error('Failed to upload document');
                return thunkAPI.rejectWithValue('Failed to upload document');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'An error occurred while uploading the document');
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Fetch all documents
export const getAllDocumentsThunk = createAsyncThunk(
    'document/getAllDocumentsThunk',
    async (
        {
            pageNumber,
            pageSize,
            sortBy = 'Id',
            sortOrder = 'asc',
            query = ''
        }: {
            pageNumber: number;
            pageSize: number;
            sortBy?: string;
            sortOrder?: string;
            query?: string
        },
        thunkAPI) => {
        try {
            const accessToken = localStorage.getItem('userToken');
            const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

            // Transform sortBy to camelCase where the first letter is uppercase
            const formattedSortBy = sortBy.charAt(0).toUpperCase() + sortBy.slice(1);

            const response = await axios.get(`${GET_DOCUMENT}`, {
                params: {
                    PageNumber: pageNumber,
                    PageSize: pageSize,
                    SortBy: formattedSortBy,
                    SortOrder: sortOrder,
                    Query: query
                },
                headers: {
                    ...headers,
                },
            });
            if (response.status === 200) {
                return response.data;
            } else {
                toast.error('Failed to fetch documents');
                return thunkAPI.rejectWithValue('Failed to fetch documents');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'An error occurred while fetching documents');
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Fetch document by ID
export const getDocumentByIdThunk = createAsyncThunk(
    'document/getDocumentByIdThunk',
    async (id: string, thunkAPI) => {
        try {
            const accessToken = localStorage.getItem('userToken');
            const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

            const response = await axios.get(`${GET_ID_DOCUMENT}/${id}`, {
                headers: {
                    ...headers,
                },
            });
            if (response.status === 200) {
                return response.data;
            } else {
                toast.error('Failed to fetch the document');
                return thunkAPI.rejectWithValue('Failed to fetch the document');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'An error occurred while fetching the document');
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Submit Document
export const submitDocumentThunk = createAsyncThunk(
    'document/submitDocumentThunk',
    async (payload: { document: any; userId: string }, thunkAPI) => {
        try {
            const accessToken = localStorage.getItem('userToken');
            const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

            const response = await axios.post(SUBMIT_DOCUMENT, payload, {
                headers: {
                    ...headers,
                    'Content-Type': 'application/json',
                },
            });
            if (response.status === 200) {
                toast.success('Document submitted successfully');
                return response.data;
            } else {
                toast.error('Failed to submit document');
                return thunkAPI.rejectWithValue('Failed to submit document');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'An error occurred while submitting the document');
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);